// TODAY'S VERSION (v1), kept beside the v1.1 one until the press (lib/release/next).
// Copied from main as it was on 2026-10-23; everyone who is not a test account sees this one.
"use client";

// ConnectionSettings — the settings section of ONE connection's window.
//
// It exists only once there is a connection to set: a tool with nothing minted
// has no settings, and no app is ever listed here that the person has not
// named or let in. Read top to bottom, it answers:
//
//   what it may read of you   the ring's depth + ask first (AccessControl), the
//                             grants it holds (each ended in a tap) and, since
//                             149, "let it read more for a while"
//   waiting on you            anything this app asked for and was not cleared
//                             for: for 30 minutes · a day · a week · always · no
//   what it may write         writes freely · asks first · read only (149), and
//                             its writes waiting for a yes
//   what it read, and when    its read receipts (149): names and counts
//   what it has been sent     every self or private fact this app was handed
//   the other end             what the connection remembers of its client
//   letting it go             asks twice, in place
//
// The ring's parts render only for someone the ring is open to (useRing says
// `hidden` otherwise); the other end and letting go are for everyone. A private
// fact is never worded here: it is named by its class and dimension.

import { useState } from "react";
import { relTime } from "../../lib/os/tree";
import { OS_WORD, readMemory } from "../../lib/connect/memory";
import { DECISION_WORDS, askHeadline, type RingApp } from "../../lib/self/ringView";
import { AllowChoices, GrantList, GrantMore, HeldWrites, Receipts, WriteControl } from "../you/RingControls";
import type { Connection } from "../../lib/connect/connections";
import type { useRing } from "../you/useRing";
import AccessControl, { readsSentence } from "./AccessControl";

type Ring = ReturnType<typeof useRing>;

const CLASS_WORDS = { personal: "self", private: "private" } as const;
const SEEN_SHOWN = 8;

const ago = (iso: string) => {
  const r = relTime(iso);
  return r === "just now" ? r : `${r} ago`;
};

function minutesLeft(until: string, now: number): number {
  return Math.max(1, Math.round((Date.parse(until) - now) / 60_000));
}

export default function ConnectionSettings({
  connection,
  name,
  ring,
  now,
  onLetGo,
}: {
  connection: Connection;
  /** what the person calls it (the freshest label, even mid-rename) */
  name: string;
  ring: Ring;
  now: number;
  onLetGo: () => void;
}) {
  const [arming, setArming] = useState(false);
  const { state, note, decide, setApp, grant, endGrant, setWrite, answerHeld } = ring;
  const data = state.status === "ready" && state.data.ready ? state.data : null;
  const app: RingApp | null = data?.apps.find((a) => a.kind === "connection" && a.id === connection.id) ?? null;
  const mine = (a: { kind: string; id: string | null }) => a.kind === "connection" && a.id === connection.id;
  const requests = data?.requests.filter((r) => mine(r.app)) ?? [];
  const windows = data?.windows.filter((w) => mine(w.app)) ?? [];
  const sent = data?.history.find((g) => mine(g.app)) ?? null;
  const passes = !!data?.passes;
  const proposals = data?.proposals.filter((p) => mine(p.app)) ?? [];
  const receipts = data?.receipts[`connection:${connection.id}`] ?? [];

  const m = readMemory(connection.memory);
  const client = m.client?.name ? (m.client.version ? `${m.client.name.toLowerCase()} ${m.client.version}` : m.client.name.toLowerCase()) : null;
  const machine = [m.device?.os ? OS_WORD[m.device.os] : null, m.device?.kind ?? null].filter(Boolean).join(" · ") || null;
  const other: Array<[string, string]> = [
    ["named", ago(connection.created_at)],
    ...(connection.first_seen_at ? ([["first call", ago(connection.first_seen_at)]] as Array<[string, string]>) : []),
    ["last call", connection.last_seen_at ? ago(connection.last_seen_at) : "not yet"],
    ...(connection.call_count ? ([["calls", connection.call_count.toLocaleString()]] as Array<[string, string]>) : []),
    ...(client ? ([["client", client]] as Array<[string, string]>) : []),
    ...(machine ? ([["machine", machine]] as Array<[string, string]>) : []),
    ...(m.door ? ([["door", m.door === "tagged" ? "its own link" : "the plain link"]] as Array<[string, string]>) : []),
  ];

  const nameId = `cset-name-${connection.id}`;

  return (
    <div className="prv cset">
      {app && (
        <section className="prv-group cset-group" aria-labelledby={nameId}>
          <h3 id={nameId} className="prv-sub">what it may read of you</h3>
          <AccessControl app={app} labelledBy={nameId} onSet={(a, d, ask) => void setApp(a, d, ask)} />
          <p className="prv-note">
            {readsSentence(app)} whatever it is sent beyond the standard is written down below.
          </p>
          {passes ? (
            <>
              <GrantList windows={windows} now={now} onEnd={(rid) => void endGrant(rid)} />
              <GrantMore app={app} onGrant={(d, l, c) => grant(app, d, l, c)} />
            </>
          ) : null}
        </section>
      )}

      {requests.length > 0 && (
        <section className="prv-group cset-group" aria-live="polite">
          <h3 className="prv-sub">waiting on you</h3>
          <div className="prv-rows">
            {requests.map((r) => (
              <div key={r.id} className="prv-row">
                <div className="prv-row-text">
                  <span className="prv-name">{askHeadline({ ...r, app: { ...r.app, label: "it" } })}</span>
                  <span className="prv-meta">
                    {r.ask ? <>for <span className="prv-ask">&ldquo;{r.ask}&rdquo;</span> · </> : null}
                    {ago(r.askedAt)}
                    {r.asks > 1 ? ` · asked ${r.asks} times` : ""}
                  </span>
                </div>
                <div className="prv-acts">
                  <AllowChoices req={r} passes={passes} onDecide={(d, how) => void decide(r.id, d, how)} />
                </div>
              </div>
            ))}
          </div>
          <p className="prv-note">
            {passes
              ? "it reads what it asked about for as long as you choose, then asks again. always lets it read them from now on."
              : "once opens a 30 minute window. always lets it read them from now on, without asking."}
          </p>
        </section>
      )}

      {app && !passes && windows.length > 0 && (
        <section className="prv-group cset-group">
          <h3 className="prv-sub">open for now</h3>
          <div className="prv-rows">
            {windows.map((w) => {
              const left = minutesLeft(w.until ?? new Date(now).toISOString(), now);
              return (
                <div key={w.requestId} className="prv-row">
                  <div className="prv-row-text">
                    <span className="prv-name">
                      it can read your {CLASS_WORDS[w.depth]} facts for {left} more minute{left === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="prv-acts">
                    <button type="button" className="quiet-link" onClick={() => void setApp(app, app.depth, true)}>
                      end it now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {app && passes && (
        <section className="prv-group cset-group" aria-labelledby={`${nameId}-w`}>
          <h3 id={`${nameId}-w`} className="prv-sub">what it may write</h3>
          <WriteControl app={app} labelledBy={`${nameId}-w`} onSet={(m) => void setWrite(app, m)} />
          <HeldWrites proposals={proposals} now={now} onAnswer={(pid, d) => void answerHeld(pid, d)} />
        </section>
      )}

      {app && passes && (
        <section className="prv-group cset-group">
          <h3 className="prv-sub">what it read, and when</h3>
          <Receipts receipts={receipts} now={now} label={name} />
        </section>
      )}

      {data && (
        <section className="prv-group cset-group">
          <h3 className="prv-sub">what it has been sent</h3>
          {!sent ? (
            <p className="prv-note">no self or private fact has left for {name}.</p>
          ) : (
            <>
              <p className="prv-note">
                {sent.facts} fact{sent.facts === 1 ? "" : "s"}, over {sent.times} call{sent.times === 1 ? "" : "s"}, the last{" "}
                {ago(sent.lastAt)}.
              </p>
              <ul className="cset-seen">
                {sent.seen.slice(0, SEEN_SHOWN).map((s, i) => (
                  <li key={`${s.factId ?? "gone"}-${i}`}>
                    <span className="prv-fact" data-d={s.disclosure}>
                      {s.factId == null
                        ? "a fact you have since forgotten"
                        : s.disclosure === "private"
                          ? `a private fact${s.dimension ? ` in ${s.dimension.replace(/_/g, " ")}` : ""}`
                          : (s.statement ?? `a self fact${s.dimension ? ` in ${s.dimension.replace(/_/g, " ")}` : ""}`)}
                    </span>
                    <span className="prv-meta">
                      {ago(s.at)} · {DECISION_WORDS[s.decision]}
                    </span>
                  </li>
                ))}
              </ul>
              {sent.seen.length > SEEN_SHOWN && (
                <p className="prv-note">and {sent.seen.length - SEEN_SHOWN} more, all on your you page.</p>
              )}
            </>
          )}
        </section>
      )}

      <section className="prv-group cset-group">
        <h3 className="prv-sub">the other end</h3>
        <div className="detail-rows cset-ledger">
          {other.map(([k, v]) => (
            <div key={k} className="detail-row">
              <span>{k}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="prv-group cset-group cset-leave">
        <p className="prv-note">
          letting it go ends its door. nothing it kept is erased, and you can connect it again any time.
        </p>
        <button
          type="button"
          className="quiet-link cset-leave-btn"
          data-armed={arming || undefined}
          onClick={() => (arming ? onLetGo() : setArming(true))}
          onBlur={() => setArming(false)}
        >
          {arming ? `press again to let ${name} go` : `let ${name} go`}
        </button>
      </section>

      <p className="prv-state" aria-live="polite">{note ?? ""}</p>
    </div>
  );
}
